import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'as' : 'en');
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/blog', label: t('nav.blog') },
    { path: '/faq', label: t('nav.faq') },
    { path: '/contact', label: t('nav.contact') }
  ];

  return (
    <>
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_63b0521a-b192-4372-9aea-5b9cab43c9ba/artifacts/ofzxk3s3_FB_IMG_1770212470970.jpg" 
                alt="Aarogya ENT Clinic Logo" 
                className="h-14 w-auto"
              />
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location.pathname === link.path ? 'text-primary' : 'text-foreground/80'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleLanguage}
                data-testid="language-toggle"
                className="rounded-full"
              >
                <Globe size={20} />
                <span className="ml-1 text-xs">{i18n.language.toUpperCase()}</span>
              </Button>
              <Link to="/appointment">
                <Button
                  className="rounded-full bg-accent hover:bg-accent/90"
                  data-testid="book-appointment-btn"
                >
                  {t('nav.bookAppointment')}
                </Button>
              </Link>
              <a href="tel:+919864815487" className="flex items-center gap-2 px-4 py-2 rounded-full bg-destructive text-white hover:bg-destructive/90 transition-colors" data-testid="emergency-call-btn">
                <Phone size={20} />
                <span className="text-sm font-semibold">{t('nav.emergency')}</span>
              </a>
            </div>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2"
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-border"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`block py-2 text-sm font-medium transition-colors ${
                    location.pathname === link.path ? 'text-primary' : 'text-foreground/80'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={toggleLanguage}
                  data-testid="mobile-language-toggle"
                >
                  <Globe size={20} className="mr-2" />
                  Switch to {i18n.language === 'en' ? 'অসমীয়া' : 'English'}
                </Button>
                <Link to="/appointment" onClick={() => setIsOpen(false)}>
                  <Button className="w-full bg-accent hover:bg-accent/90" data-testid="mobile-book-appointment-btn">
                    {t('nav.bookAppointment')}
                  </Button>
                </Link>
                <a href="tel:+919864815487" className="flex items-center justify-center gap-2 w-full px-4 py-2 rounded-md bg-destructive text-white hover:bg-destructive/90" data-testid="mobile-emergency-call-btn">
                  <Phone size={20} />
                  <span className="font-semibold">{t('nav.emergency')}</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
